import { Trans } from 'react-i18next';
import { SelectorField } from '@anytag/shared-molecules';
import { FormAction, SelectOption } from '@anytag/shared-types';
import { useDeepCompareEffect, useQueryHelper } from '@anytag/shared-hooks';
import { MarketplaceParticipateInfluencerStatus } from '@anytag/shared-organisms';
import { Form, ModalBody, BaseModal, ModalFooter, BaseModalProps, RenderDataWithFallback } from '@anytag/shared-atoms';
import {
  namedOperations,
  MarketplaceInfluencer,
  CampaignPromotionMethod,
  CampaignSocialMediaType,
  useJoinMarketplaceByStaffMutation,
  MarketplaceCampaignParticipateStatus,
  useInfluencerMultipleSocialAccountsCountLazyQuery
} from '@anytag/graphql';
import { useMarketplaceDetailsContext } from '../../../marketplace-details-context';
import {
  ConfirmInfluencersParticipateCampaignSchema,
  ConfirmInfluencersParticipateCampaignFormKeys,
  ConfirmInfluencersParticipateCampaignFormValues
} from './types';

export interface ConfirmInfluencersParticipateCampaignModalProps
  extends Omit<BaseModalProps, 'asChild' | 'footer' | 'children'> {
  resetSelectedInfluencerIds?: () => void;
  selectedInfluencerIds: Array<MarketplaceInfluencer['id']>;
}

export const ConfirmInfluencersParticipateCampaignModal = ({
  open,
  onClose,
  selectedInfluencerIds,
  resetSelectedInfluencerIds,
  ...restProps
}: ConfirmInfluencersParticipateCampaignModalProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const {
    marketplaceId,
    isAffiliateCampaign,
    marketplace: { socialMedia, isPostTracking }
  } = useMarketplaceDetailsContext();
  const {
    data,
    loading: checkingInfluencers,
    getInfluencerMultipleSocialAccountsCount
  } = useInfluencerMultipleSocialAccountsCountLazyQuery({
    fetchPolicy: 'no-cache',
    variables: { socialMedia, influencerIds: selectedInfluencerIds }
  });
  const { callJoinMarketplaceByStaff, loading: joiningInfluencers } = useJoinMarketplaceByStaffMutation({
    refetchQueries: [namedOperations.Query.AllInfluencersForMarketplaceV2]
  });

  useDeepCompareEffect(() => {
    // We don't care that campaign isAffiliateCampaign or not
    // We only care about `isPostTracking: true` because in this case always have a specific socialMedia selected, then we can check the selected influencers with that value
    if (open && isPostTracking && selectedInfluencerIds.length) {
      getInfluencerMultipleSocialAccountsCount();
    }
  }, [open, isPostTracking, selectedInfluencerIds]);

  // Only needSpecifyMethods in cases
  // 1. is Affiliate Campaign and without Post Tracking
  // 2. selected socialMedia has no specific value (UNSELECTED, OTHER), but if `isPostTracking: true` this case never happen because we required user specify a social media so we no need to check this case
  // No need to check Social Media campaign type because isPostTracking always enabled and selected socialMedia always has specific value
  const needSpecifyMethods = isAffiliateCampaign && !isPostTracking;
  const numberOfInfluencerMultipleAccounts = data?.influencerMultipleSocialAccountsCount?.totalNumber;

  const methodsOptions = needSpecifyMethods
    ? [
        ...(socialMedia === CampaignSocialMediaType.UNSELECT
          ? [
              CampaignPromotionMethod.INSTAGRAM,
              CampaignPromotionMethod.INSTAGRAM_STORY,
              CampaignPromotionMethod.FACEBOOK,
              CampaignPromotionMethod.YOUTUBE,
              CampaignPromotionMethod.TWITTER,
              CampaignPromotionMethod.TIKTOK
            ]
          : []),
        CampaignPromotionMethod.WEBSITE,
        CampaignPromotionMethod.EMAIL_NEWSLETTER,
        CampaignPromotionMethod.MOBILE_APP,
        CampaignPromotionMethod.PODCAST,
        CampaignPromotionMethod.OFFLINE
      ].map<SelectOption<CampaignPromotionMethod>>((value) => ({ value, label: t<string>(`PromotionMethod.${value}`) }))
    : [];

  const handleSubmit: FormAction<ConfirmInfluencersParticipateCampaignFormValues>['onSubmit'] = async (
    { methods },
    { reset }
  ) => {
    if (needSpecifyMethods && !methods?.length) return;

    try {
      await callJoinMarketplaceByStaff({
        variables: { input: { methods, campaignId: marketplaceId, influencerIds: selectedInfluencerIds } }
      });

      enqueueSnackbar(t('succeededInSave'), { variant: 'success' });
      resetSelectedInfluencerIds?.();
      onClose?.();
      reset();
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <BaseModal open={open} onClose={onClose} title={t('Modal.Mark as Joined')} asChild {...restProps}>
      <RenderDataWithFallback loadingProps={{ className: 'h-60' }} loading={checkingInfluencers}>
        <Form.Provider<ConfirmInfluencersParticipateCampaignFormValues>
          onSubmit={handleSubmit}
          defaultValues={{ methods: [] }}
          zodSchema={needSpecifyMethods ? ConfirmInfluencersParticipateCampaignSchema : undefined}
        >
          {({ values }) => (
            <>
              <ModalBody>
                <Trans i18nKey="Modal.MarkAsJoinedDescription" components={{ br: <br /> }} />
                {numberOfInfluencerMultipleAccounts ? (
                  <div className="mt-4">
                    <Trans
                      values={{
                        count: numberOfInfluencerMultipleAccounts,
                        status: MarketplaceCampaignParticipateStatus.WAITING
                      }}
                      i18nKey={
                        numberOfInfluencerMultipleAccounts > 1
                          ? 'Modal.MarkAsJoinedNoticePlural'
                          : 'Modal.MarkAsJoinedNoticeSingular'
                      }
                      components={{
                        1: <span css={{ fontWeight: 600 }} />,
                        2: (
                          <MarketplaceParticipateInfluencerStatus
                            status={MarketplaceCampaignParticipateStatus.WAITING}
                            css={{ height: 'max-content', display: 'inline-block', padding: '3px 8px' }}
                          />
                        )
                      }}
                    />
                  </div>
                ) : null}

                {needSpecifyMethods ? (
                  <SelectorField<ConfirmInfluencersParticipateCampaignFormKeys>
                    name="methods"
                    options={methodsOptions}
                    title={t('Selector.Select promote media')}
                    required
                    multiple
                  />
                ) : null}
              </ModalBody>

              <ModalFooter
                cancelButton={{ onClick: onClose }}
                submitButton={{
                  type: 'submit',
                  loading: joiningInfluencers,
                  title: t('Button.Mark as Joined'),
                  disabled: needSpecifyMethods && !values.methods.length
                }}
              />
            </>
          )}
        </Form.Provider>
      </RenderDataWithFallback>
    </BaseModal>
  );
};
